import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { appActions } from '../store';

/**
 * Custom hook to sync Redux state with URL
 * Swatch view: /style/Bend/size/18x18/products/-1,-1
 * Room view: /style/Bend/size/18x18/products/-1,-1/room/specifier
 */
export function useUrlSync() {
    const dispatch = useDispatch();
    const style = useSelector(state => state.app.style);
    const size = useSelector(state => state.app.size);
    const productSelection = useSelector(state => state.app.productSelection);
    const currentView = useSelector(state => state.app.currentView);
    const roomscene = useSelector(state => state.app.roomscene);

    // Load state from URL on mount
    useEffect(() => {
        const path = window.location.pathname;
        const params = parseUrlPath(path);
        
        if (params.style) {
            dispatch(appActions.setStyle(params.style));
        }
        if (params.size) {
            dispatch(appActions.setSize(params.size));
        }
        if (params.products) {
            dispatch(appActions.setProductSelection(params.products));
        }
        // If room is in URL, set room view and room scene
        if (params.room) {
            dispatch(appActions.setCurrentView('room'));
            dispatch(appActions.setRoomscene(params.room));
        } else {
            // Default to swatch view if no room in URL
            dispatch(appActions.setCurrentView('swatch'));
        }
    }, []); // Only run on mount

    // Update URL when state changes
    useEffect(() => {
        const newPath = buildUrlPath(style, size, productSelection, currentView, roomscene);
        
        // Only update if path has changed
        if (window.location.pathname !== newPath) {
            window.history.pushState({}, '', newPath);
        }
    }, [style, size, productSelection, currentView, roomscene]);
}

/**
 * Parse URL path into state object
 * Swatch: /style/Bend/size/18x18/products/-1,-1
 * Room: /style/Bend/size/18x18/products/-1,-1/room/specifier
 */
function parseUrlPath(path) {
    const params = {};
    
    // Remove leading slash and split by /
    const parts = path.replace(/^\//, '').split('/');
    
    // Parse key-value pairs
    for (let i = 0; i < parts.length; i += 2) {
        const key = parts[i];
        const value = parts[i + 1];
        
        if (key && value) {
            if (key === 'products') {
                // Parse comma-separated product IDs
                params[key] = value.split(',').map(id => {
                    const num = parseInt(id, 10);
                    return isNaN(num) ? id : num;
                });
            } else if (key === 'room') {
                // Decode room scene specifier (may contain backslashes)
                params[key] = decodeURIComponent(value);
            } else {
                params[key] = decodeURIComponent(value);
            }
        }
    }
    
    return params;
}

/**
 * Build URL path from state
 * Swatch: /style/Bend/size/18x18/products/-1,-1
 * Room: /style/Bend/size/18x18/products/-1,-1/room/specifier
 */
function buildUrlPath(style, size, productSelection, currentView, roomscene) {
    const parts = [];
    
    if (style) {
        parts.push('style', encodeURIComponent(style));
    }
    
    if (size) {
        parts.push('size', encodeURIComponent(size));
    }
    
    // Always include products, default to [-1, -1] if empty
    const products = productSelection.length > 0 
        ? productSelection.join(',') 
        : '-1';
    parts.push('products', products);
    
    // Only include room specifier when in room view
    if (currentView === 'room' && roomscene) {
        parts.push('room', encodeURIComponent(roomscene));
    }
    
    return '/' + parts.join('/');
}
